import React, { useState, useEffect } from 'react';
import {post, put} from "../../utils/fetchAPI"; // Import PUT function
import { Bounce, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CreateUser = ({ fetchUsers, organization, onCreateUser, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        alias: '',
        description: '',
        type: { code: '', display: '' },
        doctorLimit: '',
        status: '',
        contact: [{ purpose: '', name: '', contact: [{ system: '', value: '' }] }]
    });
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (organization) {
            setFormData(organization); // Populate form for editing
        }
    }, [organization]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'code' || name === 'display') {
            setFormData({
                ...formData,
                type: {
                    ...formData.type,
                    [name]: value
                }
            });
        }else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    // Handle contact details (purpose and name)
    const handleContactChange = (contactIndex, field, value) => {
        const updatedContacts = [...formData.contact];
        updatedContacts[contactIndex][field] = value;
        setFormData({ ...formData, contact: updatedContacts });
    };

    // Handle individual contact system and value
    const handleContactDetailChange = (contactIndex, detailIndex, field, value) => {
        const updatedContacts = [...formData.contact];
        updatedContacts[contactIndex].contact[detailIndex][field] = value;
        setFormData({ ...formData, contact: updatedContacts });
    };

    // Add a new contact section
    const addContact = () => {
        setFormData((prevData) => ({
            ...prevData,
            contact: [...prevData.contact, { purpose: '', name: '', contact: [{ system: '', value: '' }] }]
        }));
    };

    // Remove a contact section
    const removeContact = (contactIndex) => {
        setFormData((prevData) => ({
            ...prevData,
            contact: prevData.contact.filter((_, i) => i !== contactIndex)
        }));
    };

    // Add a new contact detail to a specific contact
    const addContactDetail = (contactIndex) => {
        const updatedContacts = [...formData.contact];
        updatedContacts[contactIndex].contact.push({ system: '', value: '' });
        setFormData({ ...formData, contact: updatedContacts });
    };

    // Remove a specific contact detail
    const removeContactDetail = (contactIndex, detailIndex) => {
        const updatedContacts = [...formData.contact];
        updatedContacts[contactIndex].contact = updatedContacts[contactIndex].contact.filter((_, i) => i !== detailIndex);
        setFormData({ ...formData, contact: updatedContacts });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return; // prevent double submit
        setIsSubmitting(true);
        if (organization) {
            // Call PUT to update organization
            put(`organization/update/${organization._id}`, formData)
                .then(async (response) => {
                    toast.success("Organization updated successfully!", {
                        position: "top-center",
                        type: "success",
                        autoClose: true,
                        progress: undefined,
                        theme: "light",
                        transition: Bounce,
                        draggable: true,
                    });
                    onCreateUser(response.data);
                    await fetchUsers();
                    onClose();
                })
                .catch((error) => {
                    console.error('Error updating organization:', error);
                }).finally(  ()=> {
                setIsSubmitting(false);
            });
        } else {
            // Call POST to create a new organization
            post('organization/create', formData)
                .then(async (response) => {
                    toast.success("Organization created successfully!", {
                        position: "top-center",
                        type: "success",
                        progress: undefined,
                        autoClose: true,
                        theme: "light",
                        transition: Bounce,
                        draggable: true,
                    });
                    onCreateUser(response.data);
                    await fetchUsers();
                    onClose();
                })
                .catch((error) => {
                    console.error('Error creating organization:', error);
                }).finally(  ()=> {
                setIsSubmitting(false);
            });
        }

    };

    return (
        <div className='py-5 px-5 flex flex-col'>
            <h2 className="text-blue-500 text-3xl font-bold mb-4">
                {organization ? "Edit Organization" : "Create New Organization"}
            </h2>
            <form onSubmit={handleSubmit}
                  className='text-black flex flex-col w-full'>
                {/* Fields for form input similar to the initial code */}
                <div className=" max-h-[30rem] overflow-y-auto">
                    <div className="flex gap-5">
                        <div className="mb-2 w-full">
                            <label className="flex py-2 text-lg">Organization Name: <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="name"
                                value={formData?.name}
                                placeholder="Organization Name"
                                onChange={handleInputChange}
                                className="text-sm border flex pl-2 py-2 rounded-md w-full"
                                required
                            />
                        </div>
                        <div className="mb-2 w-full">
                            <label className="flex py-2 text-lg">Alias: <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="alias"
                                placeholder="Alias Name"
                                value={formData?.alias}
                                onChange={handleInputChange}
                                className="text-sm border p-2 rounded-md w-full"
                                required
                            />
                        </div>
                    </div>
                    <div className="mb-2">
                        <label className="flex py-2 text-lg">Description: <span className="text-red-500">*</span></label>
                        <textarea
                            name="description"
                            placeholder="Description"
                            value={formData?.description}
                            onChange={handleInputChange}
                            className="text-sm border p-2 rounded-md w-full"
                            required
                        />
                    </div>
                    <div className="mb-2">
                        <label className="flex py-2 text-lg">Type: <span className="text-red-500">*</span></label>
                        <div className="flex gap-5">
                            <input
                                type="text"
                                name="code"
                                placeholder="Code"
                                value={formData?.type?.code}
                                onChange={handleInputChange}
                                className="text-sm border p-2 rounded-md w-full"
                                required
                            />
                            <input
                                type="text"
                                name="display"
                                placeholder="Display Name"
                                value={formData?.type?.display}
                                onChange={handleInputChange}
                                className="text-sm border p-2 rounded-md w-full"
                                required
                            />
                        </div>
                    </div>
                    <div className="flex gap-5 items-center">
                        <div className="w-full">
                            <label className="flex py-2 text-lg">Limit: <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="doctorLimit"
                                placeholder="Doctor Limit"
                                value={formData?.doctorLimit}
                                onChange={handleInputChange}
                                className="text-sm border p-2 rounded-md w-full"
                                required
                            />
                        </div>
                        <div className=" w-full">
                            <label className="flex py-2 text-lg">Status: <span className="text-red-500">*</span></label>
                            <select
                                name="status"
                                value={formData?.status}  // Use the value from the state
                                onChange={handleInputChange}
                                className="text-sm border p-2 rounded-md w-full"
                                required>
                                <option value="" disabled>Select Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="flex py-2 text-lg">Contacts: <span className="text-red-500">*</span></label>
                        {formData.contact.map((contact, contactIndex) => (
                            <div key={contactIndex} className="border p-3 mt-3 rounded">
                                <div className="flex gap-5 mb-2">
                                    <input
                                        type="text"
                                        placeholder="Purpose"
                                        value={contact?.purpose}
                                        onChange={(e) => handleContactChange(contactIndex, 'purpose', e.target.value)}
                                        className="text-sm border p-2 rounded-md w-full"
                                        required
                                    />
                                    <input
                                        type="text"
                                        placeholder="Contact Name"
                                        value={contact?.name}
                                        onChange={(e) => handleContactChange(contactIndex, 'name', e.target.value)}
                                        className="text-sm border p-2 rounded-md w-full"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeContact(contactIndex)}
                                        className="bg-red-500 text-sm text-white px-2 rounded w-[20rem]"
                                    >
                                        Remove Contact
                                    </button>
                                </div>
                                {contact.contact.map((detail, detailIndex) => (
                                    <div key={detailIndex} className="flex gap-5 mb-2">
                                        <select
                                            value={detail?.system}
                                            onChange={(e) =>
                                                handleContactDetailChange(contactIndex, detailIndex, 'system', e.target.value)
                                            }
                                            className="text-sm border p-2 rounded-md w-full"
                                            required>
                                            <option value="" disabled>Select System</option>
                                            <option value="mobile">mobile</option>
                                            <option value="fax">fax</option>
                                            <option value="email">email</option>
                                        </select>
                                        <input
                                            type="text"
                                            placeholder="Value"
                                            value={detail?.value}
                                            onChange={(e) =>
                                                handleContactDetailChange(contactIndex, detailIndex, 'value', e.target.value)
                                            }
                                            className="text-sm border p-2 rounded-md w-full"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeContactDetail(contactIndex, detailIndex)}
                                            className="bg-red-500 text-white px-2 rounded text-sm w-[20rem]"
                                        >
                                            Remove Detail
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => addContactDetail(contactIndex)}
                                    className="text-secondary px-3 py-2 rounded-md mt-2"
                                >
                                    + Add Contact Detail
                                </button>
                            </div>
                        ))}
                        <button type="button" onClick={addContact} className="text-white bg-primary px-3 py-2 rounded-md mt-3">
                            + Add New Contact
                        </button>
                    </div>
                </div>

                <div className="flex gap-5 mt-6">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`text-sm px-4 py-2 rounded-md text-white
        ${isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-primary"}
        `}
                    >
                        {isSubmitting
                            ? organization
                                ? "Updating..."
                                : "Creating..."
                            : organization
                                ? "Update"
                                : "Create"}
                    </button>

                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={onClose}
                        className={`text-sm px-4 py-2 rounded-md text-white
        ${isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-secondary"}
        `}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateUser;
